import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { AppDispatch } from "../store";

import { errorHandler } from "app/shared/Service/errorHandler";
import { alertMessage } from "../actions/common";

import {
  getAssignedStoresApi,
  getBranchesAndStoresApi,
  getConfigurationsApi,
  getPermissionsByPrivilegeIdApi,
  getPermissionsListApi,
  getPrivilegesApi,
  getStaffListApi,
  getStaffPermissionsApi,
  postAssignPermissionToPrivilegeApi,
  postAssignStoreApi,
  postRemovePermissionFromPrivilegeApi,
  postStaffApi,
  putConfigurationsApi,
  putPermissionsApi,
  putStaffApi,
  removeAssignStoreApi,
} from "app/services/permissions";
import {
  AssignedStores,
  AssignStoreApiPayload,
  BranchesAndStores,
  Configurations,
  EnhancedPermission,
  EnhancedPermissionTableRow,
  Permission,
  PermissionsDetails,
  PermissionSection,
  PostStaffApiPayload,
  Privilege,
  Staff,
} from "app/types/permissions";
import { PaginationInterfaceLF } from "app/types";
import { RootState } from "../reducers/hooks";
import { createQuery, getActiveConfig } from "app/utils/constants/common";
interface StaffFilters {
  search: string;
}
interface InitialState {
  permissionsList: Permission[];
  permissionSections: PermissionSection[];
  isPermissionsListLoading: boolean;
  staffList: Staff[];
  staffListPagination: PaginationInterfaceLF;
  privilegesList: Privilege[];
  defaultStaffPermissions: PermissionsDetails[];
  overrideStaffPermissions: PermissionsDetails[];
  staffListRowsPerPage: number;
  isStaffListLoading?: boolean;
  filters: StaffFilters | null;
  assignedStores: AssignedStores[];
  branchesAndStores: BranchesAndStores[];
  privilegesAssignedPermissions: {
    privilegeId: number;
    permissions: EnhancedPermissionTableRow[];
  }[];

  configurationData: Configurations | null;
}

const initialState: InitialState = {
  permissionsList: [],
  permissionSections: [],
  isPermissionsListLoading: false,
  staffList: [],
  staffListPagination: null,
  privilegesList: [],
  defaultStaffPermissions: [],
  overrideStaffPermissions: [],
  staffListRowsPerPage: 20,
  isStaffListLoading: false,
  filters: null,
  assignedStores: [],
  branchesAndStores: [],
  privilegesAssignedPermissions: [],
  configurationData: null,
};

export const permissionsSlice = createSlice({
  name: "permissions",
  initialState,
  reducers: {
    setPermissionsList: (state, action: PayloadAction<Permission[]>) => {
      state.permissionsList = action.payload;
    },

    setIsPermissionsListLoading: (state, action: PayloadAction<boolean>) => {
      state.isPermissionsListLoading = action.payload;
    },
    setStaffList: (
      state,
      action: PayloadAction<{
        enhancedStaffList: Staff[];
        pagination: PaginationInterfaceLF;
      }>,
    ) => {
      state.staffList = action.payload.enhancedStaffList;
      state.staffListPagination = action.payload.pagination;
    },
    setPrivilegesList: (state, action: PayloadAction<Privilege[]>) => {
      state.privilegesList = action.payload;
    },
    setStaffFilters: (state, action: PayloadAction<StaffFilters | null>) => {
      state.filters = action.payload;
    },
    updateStaffInList: (state, action: PayloadAction<Staff>) => {
      const index = state.staffList.findIndex(
        (staff) => staff.id === action.payload.id,
      );
      if (index !== -1) {
        state.staffList[index] = {
          ...state.staffList[index],
          ...action.payload,
        };
      }
    },
    setStaffPermissionsList: (
      state,
      action: PayloadAction<{
        defaultPermissions: PermissionsDetails[];
        overridePermissions: PermissionsDetails[];
      }>,
    ) => {
      state.defaultStaffPermissions = action.payload.defaultPermissions;
      state.overrideStaffPermissions = action.payload.overridePermissions;
    },
    setStaffListRowsPerPage: (state, action: PayloadAction<number>) => {
      state.staffListRowsPerPage = action.payload;
    },
    setIsStaffListLoading: (state, action: PayloadAction<boolean>) => {
      state.isStaffListLoading = action.payload;
    },
    setAssignedStoresList: (state, action: PayloadAction<AssignedStores[]>) => {
      state.assignedStores = action.payload;
    },
    setBranchesAndStores: (
      state,
      action: PayloadAction<BranchesAndStores[]>,
    ) => {
      state.branchesAndStores = action.payload;
    },
    setPrivilegesAssignedPermissions: (
      state,
      action: PayloadAction<{
        privilegeId: number;
        permissions: EnhancedPermissionTableRow[];
      }>,
    ) => {
      const existingIndex = state.privilegesAssignedPermissions.findIndex(
        (item) => item.privilegeId === action.payload.privilegeId,
      );
      if (existingIndex !== -1) {
        state.privilegesAssignedPermissions[existingIndex].permissions =
          action.payload.permissions;
      } else {
        state.privilegesAssignedPermissions.push({
          privilegeId: action.payload.privilegeId,
          permissions: action.payload.permissions,
        });
      }
    },
    setConfigurationData: (
      state,
      action: PayloadAction<Configurations | null>,
    ) => {
      state.configurationData = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setIsPermissionsListLoading,
  setPermissionsList,
  setStaffList,
  setPrivilegesList,
  setStaffPermissionsList,
  setStaffListRowsPerPage,
  setIsStaffListLoading,
  setStaffFilters,
  updateStaffInList,
  setAssignedStoresList,
  setBranchesAndStores,
  setPrivilegesAssignedPermissions,
  setConfigurationData,
} = permissionsSlice.actions;
export default permissionsSlice.reducer;

export const getPermissionsListAction = () => {
  return async (dispatch: AppDispatch) => {
    try {
      const { data } = await getPermissionsListApi();
      const permissions = data.data || [];
      const permissionSections = data.included?.permissionSections || [];

      // Build map of section ID -> section name
      const sectionMap = permissionSections.reduce(
        (acc: Record<number, string>, section: PermissionSection) => {
          acc[section.id] = section.name;
          return acc;
        },
        {},
      );

      // Enhance each permission with its section name
      const enhancedPermissions = permissions.map((permission: Permission) => ({
        ...permission,
        sectionName:
          sectionMap[permission.permissionSectionId] || "Unknown Section",
      }));

      dispatch(setPermissionsList(enhancedPermissions));
      return data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};
type AddPermissions = { permissionId: number; action: string };
type RemovePermissions = { permissionId: number };

export type AssignPermissionsPayload = {
  Add?: AddPermissions[];
  Remove?: RemovePermissions[];
};

export const assignPermissionsAction = ({
  identityId,
  payload,
}: {
  identityId: number;
  payload: AssignPermissionsPayload;
}) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { data } = await putPermissionsApi(identityId, payload);
      dispatch(getStaffPermissionsListAction(identityId));
      dispatch(alertMessage("Updated successfully", "success"));
      return data?.data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const getStaffListAction = ({
  continutationToken = null,
}: {
  continutationToken: string | null;
}) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const { staffListRowsPerPage, filters } = getState().permissions;

      // Build the query with optional search parameter
      const query =
        "?" +
        createQuery({
          filter: {
            ...filters,
            limit: staffListRowsPerPage,
            continutationToken,
            // Only pass search if it exists
          },
        });

      // Start loading state
      dispatch(setIsStaffListLoading(true));

      // Call the API with the query string
      const { data } = await getStaffListApi(query);

      // Stop loading state
      dispatch(setIsStaffListLoading(false));

      const pagination = data.pagination;
      const staffList = data.data;
      const privileges = data.included.privileges;
      const privilegesMap = privileges.reduce(
        (acc: Record<number, string>, privilege: Privilege) => {
          acc[privilege.id] = privilege.name;
          return acc;
        },
        {},
      );
      const enhancedStaffList = staffList.map((staffs: Staff) => ({
        ...staffs,
        privilegeName: privilegesMap[staffs.privilegeId] || "Unknown",
      }));
      // Dispatch action to set staff list data
      dispatch(setStaffList({ enhancedStaffList, pagination }));
    } catch (error) {
      dispatch(setIsStaffListLoading(false));
      // Handle error
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const postStaffsAction = (payload: PostStaffApiPayload) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { data } = await postStaffApi(payload);
      dispatch(alertMessage("Staff added successfully", "success"));
      return data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const putStaffsAction = (
  identityId: number,
  payload: PostStaffApiPayload,
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const { data } = await putStaffApi(identityId, payload);
      const state = getState();
      const existingStaff = state.permissions.staffList.find(
        (s) => s.id === identityId,
      );

      if (!existingStaff) {
        return;
      }

      const privilege = state.permissions.privilegesList.find(
        (p) => p.id === payload.privilegeId,
      );

      // Merge existing staff with updated fields
      const updatedStaff: Staff = {
        ...existingStaff,
        ...payload,
        privilegeName: privilege?.name || "Unknown",
      };
      dispatch(getStaffPermissionsListAction(identityId));
      dispatch(updateStaffInList(updatedStaff));
      dispatch(alertMessage("Staff details updated successfully", "success"));
      return data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const getPrivilegesListAction = () => {
  return async (dispatch: AppDispatch) => {
    try {
      const { data } = await getPrivilegesApi();
      dispatch(setPrivilegesList(data.data || []));
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const getStaffPermissionsListAction = (identityId: number) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { data } = await getStaffPermissionsApi(identityId);
      const defaultPermissions = data.data.defaults || [];
      const overridePermissions = data.data.overrides || [];
      dispatch(
        setStaffPermissionsList({ defaultPermissions, overridePermissions }),
      );
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const getAssignedStoresListAction = (id: number) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { data } = await getAssignedStoresApi(id);
      dispatch(setAssignedStoresList(data.data || []));
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const getBranchesAndStoresAction = () => {
  return async (dispatch: AppDispatch) => {
    try {
      const { data } = await getBranchesAndStoresApi();
      dispatch(setBranchesAndStores(data.data || []));
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const postAssignStoreAction = (payload: AssignStoreApiPayload) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { data } = await postAssignStoreApi(payload);
      dispatch(getAssignedStoresListAction(payload.staffIdentityId));
      dispatch(alertMessage("Store Assigned Successfully", "success"));
      return data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const removeAssignStoreAction = (payload: AssignStoreApiPayload) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { data } = await removeAssignStoreApi(payload);
      dispatch(getAssignedStoresListAction(payload.staffIdentityId));
      dispatch(alertMessage("Store removed Successfully", "success"));
      return data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const getPermissionsByPrivilegeIdAction = (privilegeId: number) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { data } = await getPermissionsByPrivilegeIdApi(privilegeId);
      const permissionIds = data.data || [];
      const permissions = data.included.permissions || [];
      const permissionSections = data.included.permissionSections || [];

      // Build map of section ID -> section name
      const sectionMap = permissionSections.reduce(
        (acc: Record<number, string>, section: PermissionSection) => {
          acc[section.id] = section.name;
          return acc;
        },
        {},
      );

      // Loop through permissionIds and find matching permissions to enhance.
      // And return the enhanced list permission list with section names.
      const enhancedPermissions: EnhancedPermission[] = permissionIds.map(
        (perm) => {
          const matchedPermission = permissions.find(
            (p) => p.id === perm.permissionId,
          );
          return {
            ...matchedPermission,
            sectionName: matchedPermission?.permissionSectionId
              ? sectionMap[matchedPermission.permissionSectionId] ||
                "Unknown Section"
              : "Uncategorized",
            effect: perm.effect,
          };
        },
      );

      // Group permissions by section
      const grouped = enhancedPermissions.reduce(
        (acc: Record<string, EnhancedPermission[]>, item) => {
          const section = item.sectionName || "Uncategorized";
          if (!acc[section]) acc[section] = [];
          acc[section].push(item);
          return acc;
        },
        {},
      );

      const flattenedData: EnhancedPermissionTableRow[] = Object.entries(
        grouped,
      ).flatMap(([section, perms]) => [
        { isSection: true as const, section },
        ...perms.map((perm) => ({
          ...perm,
          isSection: false as const,
        })),
      ]);

      dispatch(
        setPrivilegesAssignedPermissions({
          privilegeId,
          permissions: flattenedData,
        }),
      );

      return flattenedData;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const assignPermissionToPrivilegeAction = ({
  privilegeId,
  permissions,
}: {
  privilegeId: number;
  permissions: { permissionId: number; effect: "Allow" | "Deny" }[];
}) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { data } = await postAssignPermissionToPrivilegeApi({
        privilegeId,
        permissions,
      });
      dispatch(alertMessage("Permissions assigned successfully", "success"));
      return data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const removePermissionFromPrivilegeAction = ({
  privilegeId,
  permissions,
}: {
  privilegeId: number;
  permissions: number[];
}) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { data } = await postRemovePermissionFromPrivilegeApi(
        privilegeId,
        permissions,
      );
      dispatch(alertMessage("Permissions removed successfully", "success"));
      return data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const getConfigurationsAction = () => {
  return async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      const { data } = await getConfigurationsApi(storeId);
      dispatch(setConfigurationData(data.data || null));
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const putConfigurationsAction = (payload: Configurations) => {
  return async (dispatch: AppDispatch) => {
    try {
      const { storeId } = getActiveConfig();
      const { data } = await putConfigurationsApi(storeId, payload);
      dispatch(alertMessage("Configuration Updated successfully", "success"));
      return data;
    } catch (error) {
      const message = errorHandler(error);
      dispatch(alertMessage(message, "error"));
    }
  };
};
